import { useState } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import FloatingCTA from '../../components/feature/FloatingCTA';
import { faqs } from '../../mocks/faqData';
import { Link } from 'react-router-dom';

const categories = [
  { key: 'all', label: 'All Questions' },
  { key: 'chimney', label: 'Chimney Cleaning' },
  { key: 'airduct', label: 'Air Duct Cleaning' },
  { key: 'dryervent', label: 'Dryer Vent Cleaning' },
  { key: 'general', label: 'General' },
];

export default function FAQPage() {
  const [openId, setOpenId] = useState<string | null>(null);
  const [activeCategory, setActiveCategory] = useState<string>('all');

  const filtered = activeCategory === 'all' ? faqs : faqs.filter((f) => f.category === activeCategory);

  return (
    <>
      <Header />
      <FloatingCTA />

      {/* Hero */}
      <section className="pt-32 pb-16 bg-brand-dark">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 text-brand-amber text-xs font-bold uppercase tracking-widest mb-5">
            <div className="h-px w-8 bg-brand-amber" />
            FAQ
            <div className="h-px w-8 bg-brand-amber" />
          </div>
          <h1 className="font-serif text-5xl md:text-6xl font-bold text-white mb-5">
            Frequently Asked Questions
          </h1>
          <p className="text-white/70 text-xl">Everything you need to know about chimney cleaning, air duct cleaning, and dryer vent cleaning in Jacksonville, FL.</p>
        </div>
      </section>

      {/* FAQ content */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Category filter */}
          <div className="flex flex-wrap gap-2 mb-10 justify-center">
            {categories.map((cat) => (
              <button
                key={cat.key}
                onClick={() => setActiveCategory(cat.key)}
                className={`px-4 py-2 rounded-full text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${
                  activeCategory === cat.key
                    ? 'bg-brand-amber text-white'
                    : 'bg-stone-100 text-brand-light hover:bg-amber-50 hover:text-brand-amber'
                }`}
              >
                {cat.label}
              </button>
            ))}
          </div>

          {/* Schema */}
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify({
                '@context': 'https://schema.org',
                '@type': 'FAQPage',
                mainEntity: faqs.map((f) => ({
                  '@type': 'Question',
                  name: f.question,
                  acceptedAnswer: { '@type': 'Answer', text: f.answer },
                })),
              }),
            }}
          />

          {/* FAQs */}
          <div className="space-y-3">
            {filtered.map((faq) => (
              <div key={faq.id} className={`border rounded-xl overflow-hidden transition-all ${openId === faq.id ? 'border-brand-amber/40 bg-amber-50/30' : 'border-stone-200 hover:border-stone-300'}`}>
                <button className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left cursor-pointer" onClick={() => setOpenId(openId === faq.id ? null : faq.id)}>
                  <span className={`font-semibold text-base leading-snug ${openId === faq.id ? 'text-brand-amber' : 'text-brand-dark'}`}>{faq.question}</span>
                  <div className={`w-8 h-8 flex items-center justify-center rounded-full flex-shrink-0 transition-colors ${openId === faq.id ? 'bg-brand-amber text-white' : 'bg-stone-100 text-brand-warm-gray'}`}>
                    <i className={`${openId === faq.id ? 'ri-subtract-line' : 'ri-add-line'} text-sm`} />
                  </div>
                </button>
                {openId === faq.id && (
                  <div className="px-6 pb-5">
                    <p className="text-brand-warm-gray text-sm leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="mt-12 p-8 bg-brand-muted rounded-2xl border border-stone-200 text-center">
            <h3 className="font-serif text-2xl font-bold text-brand-dark mb-3">Still Have Questions?</h3>
            <p className="text-brand-warm-gray mb-6">Our team is happy to help. Call us or send a message and we'll get back to you right away.</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact" className="bg-brand-amber hover:bg-brand-amber-dark text-white font-bold py-3 px-8 rounded-lg text-sm cursor-pointer whitespace-nowrap transition-all">Contact Us</Link>
              <a href="tel:+19045550191" className="border border-stone-300 hover:border-brand-amber text-brand-dark hover:text-brand-amber font-semibold py-3 px-8 rounded-lg text-sm cursor-pointer whitespace-nowrap transition-all">Call (904) 555-0191</a>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
}
